import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Center,
  Divider,
  HStack,
  Stack,
  Text,
  Button,
  useToast,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import {
  ConnectionMode,
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  MarkerType,
  useReactFlow,
  getConnectedEdges,
  getIncomers,
  getOutgoers,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { doc, onSnapshot } from 'firebase/firestore';
import { toPng } from 'html-to-image';
import dagre from 'dagre';
import {
  FcDownload,
  FcRedo,
  FcShare,
  FcUndo,
} from 'react-icons/fc';
import { FiAlignCenter, FiPlus } from 'react-icons/fi';

import { db } from '../../Config/firebase';
import useUserStore from '../../Hooks/Zustand/Store';
import { useFlowchartStoreMindmap } from '../../Hooks/Zustand/reactFlow';
import useUndoRedo from '../../Components/MindMap/useUndoRedo';
import { arrayUnionFirebase, setDocumentFirebase } from '../../Api/firebaseApi';
import { clientTypessense } from '../../Api/Typesense';
import edgeTypes from './Mindmap/EdgeType';
import nodeTypes from './Mindmap/NodeType';
import BackButtons from '../../Components/Buttons/BackButtons';
import ModalAddUser from '../Workflow/Components/ModalAddUser';

// ==================== CONSTANTS ====================
const PRO_OPTIONS = { account: 'paid-pro', hideAttribution: true };
const FIT_VIEW_OPTIONS = { padding: 0.2, duration: 300 };
const DEFAULT_EDGE_OPTIONS = {
  type: 'smoothstep',
  markerEnd: { type: MarkerType.ArrowClosed },
  style: { strokeWidth: 3 },
};
const NODE_WIDTH = 172;
const NODE_HEIGHT = 36;

// ==================== AUTO LAYOUT ====================
const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// ==================== MAIN COMPONENT ====================
function Mindmap() {
  const { t } = useTranslation();
  const { id } = useParams();
  const toast = useToast();
  const globalState = useUserStore();
  const reactFlowInstance = useReactFlow();

  // Store
  const {
    nodes,
    edges,
    onConnect,
    setNodes,
    setEdges,
    onEdgesChange,
    onNodesChange,
    title,
    setTitle,
    owner,
    setOwner,
  } = useFlowchartStoreMindmap();

  // Undo/Redo
  const { undo, redo, canUndo, canRedo, takeSnapshot } = useUndoRedo();

  // Local state
  const [data, setData] = useState(null);
  const [access, setAccess] = useState('');
  const [searchResult, setSearchResult] = useState([]);
  const [modalProjectUser, setModalProjectUser] = useState(false);
  const [selectedUserProjectIds, setSelectedUserProjectIds] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);

  // ==================== FETCH MINDMAP ====================
  const getMindmap = useCallback(() => {
    const docRef = doc(db, 'flowcharts', id);

    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const mindmapData = docSnapshot.data();
          setNodes(mindmapData.nodes || []);
          setEdges(mindmapData.edges || []);
          setTitle(mindmapData.title || '');
          setOwner(mindmapData.owner?.includes(globalState.uid) || false);
          setData(mindmapData);
        }
      },
      (error) => {
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    );

    return unsubscribe;
  }, [id, globalState.uid, setNodes, setEdges, setTitle, setOwner, toast]);

  useEffect(() => {
    const unsubscribe = getMindmap();
    return () => {
      unsubscribe();
      setData(null);
      setEdges([]);
      setNodes([]);
      setAccess('');
      setSelectedUserProjectIds([]);
      setOwner(false);
      setSearchResult([]);
      setModalProjectUser(false);
    };
  }, [getMindmap]);

  // ==================== KEYBOARD SHORTCUTS ====================
  const onKeyDown = useCallback(
    (event) => {
      // Enter - Add child node
      if (event.key === 'Enter' && selectedNode) {
        event.preventDefault();
        addChildNode(selectedNode);
      }

      // Tab - Add sibling node
      if (event.key === 'Tab' && selectedNode) {
        event.preventDefault();
        addSiblingNode(selectedNode);
      }
    },
    [selectedNode, nodes, edges]
  );

  // ==================== NODE OPERATIONS ====================
  
  /**
   * Add child node
   */
  const addChildNode = useCallback(
    (parentNode) => {
      if (!parentNode) return;

      takeSnapshot();

      const newNodeId = `node-${Date.now()}`;
      const newNode = {
        id: newNodeId,
        type: 'custom',
        position: {
          x: parentNode.position.x,
          y: parentNode.position.y + 150,
        },
        data: { label: 'New Node' },
      };

      const newEdge = {
        id: `edge-${parentNode.id}-${newNodeId}`,
        source: parentNode.id,
        target: newNodeId,
        type: 'floating',
        markerEnd: { type: MarkerType.ArrowClosed },
      };

      setNodes([...nodes, newNode]);
      setEdges([...edges, newEdge]);

      toast({
        title: 'Node added',
        status: 'success',
        duration: 1000,
      });
    },
    [nodes, edges, setNodes, setEdges, takeSnapshot, toast]
  );

  /**
   * Add sibling node
   */
  const addSiblingNode = useCallback(
    (selectedNode) => {
      if (!selectedNode) return;

      takeSnapshot();

      const parentEdge = edges.find((edge) => edge.target === selectedNode.id);

      const newNodeId = `node-${Date.now()}`;
      const newNode = {
        id: newNodeId,
        type: 'custom',
        position: {
          x: selectedNode.position.x + 200,
          y: selectedNode.position.y,
        },
        data: { label: 'New Node' },
      };

      let newEdges = [...edges];

      if (parentEdge) {
        const newEdge = {
          id: `edge-${parentEdge.source}-${newNodeId}`,
          source: parentEdge.source,
          target: newNodeId,
          type: 'floating',
          markerEnd: { type: MarkerType.ArrowClosed },
        };
        newEdges = [...edges, newEdge];
      }

      setNodes([...nodes, newNode]);
      setEdges(newEdges);

      toast({
        title: 'Sibling added',
        status: 'success',
        duration: 1000,
      });
    },
    [nodes, edges, setNodes, setEdges, takeSnapshot, toast]
  );

  /**
   * Quick add node
   */
  const quickAddNode = useCallback(() => {
    takeSnapshot();

    const viewport = reactFlowInstance.getViewport();
    const newNodeId = `node-${Date.now()}`;

    const newNode = {
      id: newNodeId,
      type: 'custom',
      position: {
        x: (window.innerWidth / 2 - viewport.x) / viewport.zoom,
        y: (window.innerHeight / 2 - viewport.y) / viewport.zoom,
      },
      data: { label: 'New Node' },
    };

    setNodes([...nodes, newNode]);

    toast({
      title: 'Node created',
      status: 'success',
      duration: 1500,
    });
  }, [nodes, setNodes, reactFlowInstance, takeSnapshot, toast]);

  /**
   * Auto layout
   */
  const autoLayout = useCallback(() => {
    takeSnapshot();

    const { nodes: layoutedNodes, edges: layoutedEdges } =
      getLayoutedElements(nodes, edges, 'TB');

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);

    setTimeout(() => {
      reactFlowInstance.fitView(FIT_VIEW_OPTIONS);
    }, 50);

    toast({
      title: 'Layout applied',
      status: 'success',
      duration: 1500,
    });
  }, [nodes, edges, setNodes, setEdges, reactFlowInstance, takeSnapshot, toast]);

  // ==================== EVENT HANDLERS ====================
  
  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const onNodeDragStart = useCallback(() => {
    takeSnapshot();
  }, [takeSnapshot]);

  const onSelectionDragStart = useCallback(() => {
    takeSnapshot();
  }, [takeSnapshot]);

  const onNodesDelete = useCallback(
    (deletedNodes) => {
      const newEdges = deletedNodes.reduce((acc, node) => {
        const incomers = getIncomers(node, nodes, edges);
        const outgoers = getOutgoers(node, nodes, edges);
        const connectedEdges = getConnectedEdges([node], edges);

        const remainingEdges = acc.filter(
          (edge) => !connectedEdges.includes(edge)
        );

        const createdEdges = incomers.flatMap(({ id: source }) =>
          outgoers.map(({ id: target }) => ({
            id: `${source}->${target}`,
            source,
            target,
            type: 'floating',
            markerEnd: { type: MarkerType.ArrowClosed },
          }))
        );

        return [...remainingEdges, ...createdEdges];
      }, edges);

      setEdges(newEdges);
      takeSnapshot();
    },
    [nodes, edges, setEdges, takeSnapshot]
  );

  /**
   * Download mindmap as PNG
   */
  const downloadButton = useCallback(async () => {
    try {
      const dataUrl = await toPng(
        document.querySelector('.react-flow__renderer'),
        {
          style: { width: 1024, height: 768 },
        }
      );

      const a = document.createElement('a');
      a.setAttribute('download', `${title || 'mindmap'}.png`);
      a.setAttribute('href', dataUrl);
      a.click();

      toast({
        title: t('success'),
        description: 'Mindmap downloaded',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to download',
        status: 'error',
        duration: 3000,
      });
    }
  }, [title, toast, t]);

  /**
   * Save mindmap
   */
  const save = useCallback(async () => {
    try {
      const newData = {
        edges,
        nodes,
        lastUpdated: new Date(),
        lastUpdatedBy: {
          uid: globalState.uid,
          email: globalState.email,
        },
        companyId: globalState.currentCompany,
        projectId: globalState.currentProject,
      };

      const res = await setDocumentFirebase('flowcharts', id, newData);

      toast({
        title: t('saved'),
        description: res.message,
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: t('toast.error'),
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  }, [edges, nodes, globalState, id, toast, t]);

  /**
   * Search users
   */
  const handleSearchUsers = useCallback(
    (query) => {
      const companyUsers = globalState.companies.find(
        (x) => x.id === globalState.currentCompany
      );

      if (!companyUsers?.users) return;

      const chunkArray = (arr, size) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += size) {
          chunks.push(arr.slice(i, i + size));
        }
        return chunks;
      };

      const userChunks = chunkArray(companyUsers.users, 100);

      const searchPromises = userChunks.map((userChunk) => {
        const searchParameters = {
          q: query,
          query_by: 'name,email',
          filter_by: `id: [${userChunk.join(',')}]`,
          sort_by: '_text_match:desc',
        };

        return clientTypessense
          .collections('users')
          .documents()
          .search(searchParameters);
      });

      Promise.all(searchPromises)
        .then((results) => {
          const combinedResults = results.flatMap((result) => result.hits);
          setSearchResult(combinedResults);
        })
        .catch((error) => {
          toast({
            title: 'Error',
            description: error.message,
            status: 'error',
            duration: 3000,
          });
        });
    },
    [globalState.companies, globalState.currentCompany, toast]
  );

  const handleUserProjectClick = useCallback((userId) => {
    setSelectedUserProjectIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  }, []);

  const handleAddTeamProject = useCallback(async () => {
    if (!access) {
      toast({
        title: t('toast.error'),
        description: 'Please select an access level',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      const collectionName = `flowcharts/${id}/users`;
      const userIds = selectedUserProjectIds.map((x) => x.id);

      await Promise.all(
        selectedUserProjectIds.map((user) =>
          setDocumentFirebase(collectionName, user.id, user)
        )
      );

      await arrayUnionFirebase('flowcharts', id, 'users', userIds);

      if (access === 'editor') {
        await arrayUnionFirebase('flowcharts', id, 'owner', userIds);
      }

      toast({
        title: t('success'),
        description: 'Users added successfully',
        status: 'success',
        duration: 2000,
      });

      setModalProjectUser(false);
      setSelectedUserProjectIds([]);
      setSearchResult([]);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  }, [access, id, selectedUserProjectIds, toast, t]);

  // ==================== PANEL COMPONENT ====================
  const PanelControl = () => {
    const isOwner = data?.owner?.includes(globalState?.uid);

    return (
      <Panel position="top-right">
        <HStack
          bgColor="white"
          p={2}
          px={4}
          borderRadius="3xl"
          boxShadow="rgba(99, 99, 99, 0.2) 0px 2px 8px 0px"
          spacing={2}
        >
          <Text color="lightgrey" fontSize="xs">
            {t('controlPanel')}
          </Text>

          <Center height="30px">
            <Divider orientation="vertical" />
          </Center>

          <Tooltip label="Download">
            <IconButton
              icon={<FcDownload />}
              onClick={downloadButton}
              size="sm"
              variant="ghost"
              aria-label="Download"
            />
          </Tooltip>

          {isOwner && (
            <>
              <Tooltip label="Undo (Ctrl+Z)">
                <IconButton
                  icon={<FcUndo />}
                  onClick={undo}
                  isDisabled={!canUndo}
                  size="sm"
                  variant="ghost"
                  aria-label="Undo"
                />
              </Tooltip>

              <Tooltip label="Redo (Ctrl+Y)">
                <IconButton
                  icon={<FcRedo />}
                  onClick={redo}
                  isDisabled={!canRedo}
                  size="sm"
                  variant="ghost"
                  aria-label="Redo"
                />
              </Tooltip>

              <Tooltip label="Add Node">
                <IconButton
                  icon={<FiPlus />}
                  onClick={quickAddNode}
                  size="sm"
                  colorScheme="green"
                  aria-label="Add Node"
                />
              </Tooltip>

              <Tooltip label="Auto Layout">
                <IconButton
                  icon={<FiAlignCenter />}
                  onClick={autoLayout}
                  size="sm"
                  colorScheme="purple"
                  aria-label="Auto Layout"
                />
              </Tooltip>

              <Tooltip label="Share">
                <IconButton
                  icon={<FcShare />}
                  onClick={() => setModalProjectUser(true)}
                  size="sm"
                  variant="ghost"
                  aria-label="Share"
                />
              </Tooltip>

              <Button colorScheme="blue" size="sm" onClick={save}>
                {t('save')}
              </Button>
            </>
          )}
        </HStack>
      </Panel>
    );
  };

  // ==================== RENDER ====================
  return (
    <Center>
      <Stack
        w="full"
        h="100vh"
        border="1px"
        borderColor="gray.400"
        bgColor="gray.200"
        borderRadius="lg"
        onKeyDown={onKeyDown}
        tabIndex={0}
      >
        <div
          className="simple-floatingedges"
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#171523',
          }}
        >
          <ReactFlow
            fitView
            nodes={nodes}
            edges={edges}
            onConnect={onConnect}
            defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
            edgeTypes={edgeTypes}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodesDelete={onNodesDelete}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            fitViewOptions={FIT_VIEW_OPTIONS}
            onNodeDragStart={onNodeDragStart}
            onSelectionDragStart={onSelectionDragStart}
            proOptions={PRO_OPTIONS}
            connectionMode={ConnectionMode.Loose}
            deleteKeyCode={['Delete', 'Backspace']}
          >
            <Controls />
            <MiniMap />

            {/* Title Panel */}
            <Panel position="top-left">
              <HStack
                bgColor="white"
                p={2}
                px={7}
                borderRadius="3xl"
                boxShadow="rgba(99, 99, 99, 0.2) 0px 2px 8px 0px"
              >
                <BackButtons />
                <Center height="30px">
                  <Divider orientation="vertical" />
                </Center>
                <Text color="darkgray">{title}</Text>
              </HStack>
            </Panel>

            {/* Control Panel */}
            <PanelControl />

            {/* Keyboard Shortcuts Helper */}
            <Panel position="bottom-left">
              <Stack
                bgColor="white"
                p={3}
                borderRadius="md"
                boxShadow="sm"
                fontSize="xs"
                spacing={1}
              >
                <Text fontWeight="bold">⌨️ Shortcuts:</Text>
                <Text>• <strong>Enter</strong> - Add child</Text>
                <Text>• <strong>Tab</strong> - Add sibling</Text>
                <Text>• <strong>Delete</strong> - Remove</Text>
                {selectedNode && (
                  <Text color="blue.500">✓ Node selected</Text>
                )}
              </Stack>
            </Panel>
          </ReactFlow>
        </div>

        {/* Share Modal */}
        <ModalAddUser
          title="Mindmap"
          modalProjectUser={modalProjectUser}
          setModalProjectUser={setModalProjectUser}
          handleSearchUsers={handleSearchUsers}
          searchResult={searchResult}
          handleUserProjectClick={handleUserProjectClick}
          owner={owner}
          access={access}
          setAccess={setAccess}
          selectedUserProjectIds={selectedUserProjectIds}
          handleAddTeamProject={handleAddTeamProject}
        />
      </Stack>
    </Center>
  );
}

// ==================== WRAPPER ====================
function ProductivityMindMapIndex() {
  return (
    <ReactFlowProvider>
      <Mindmap />
    </ReactFlowProvider>
  );
}

export default ProductivityMindMapIndex;